import {
  confirmCodeSlug,
  fetchAddress,
  fetchAddressSlug,
  fetchAvailability,
  fetchAvailabilitySlug,
  fetchCoverage,
  fetchCoverageSlug,
  fetchPublicShop,
  fetchPublicSlug,
  postBooking,
  postBookingSlug,
  resendCodeSlug,
  suggestAddresses,
  suggestAddressesSlug,
} from './v2Api';
import { viaRetenu } from './via';

/**
 * La couche d'accès aux données du tunnel : d'où viennent la fiche du compte et
 * les horaires, et où part la demande. Le tunnel ne connaît que cette forme,
 * `{ cle, chargerCompte, chargerDispo, envoyer }`, plus les trois lectures qui
 * situent l'adresse (`suggererAdresses`, `detaillerAdresse`,
 * `chargerCouverture`, FIXERLIGHT-048/073).
 *
 * Ce fichier est séparé de `v2Api.js` à dessein : les fonctions sont appelées
 * par leur import, donc un test qui les remplace (`jest.mock('./v2Api')`)
 * remplace aussi ce que le tunnel appelle.
 */

/**
 * Sur la fiche annuaire : `public/shops/{id}`, avec le compte du client. La
 * demande porte le canal du réparateur retenu à l'arrivée (`./via`).
 */
export const accesAnnuaire = (shopId) => ({
  cle: shopId,
  chargerCompte: () => fetchPublicShop(shopId),
  chargerDispo: (from, to, service) =>
    fetchAvailability(shopId, from, to, service),
  envoyer: (body) => postBooking(shopId, { ...body, ...viaRetenu(shopId) }),
  suggererAdresses: (texte, session) =>
    suggestAddresses(shopId, texte, session),
  detaillerAdresse: (placeId, session) =>
    fetchAddress(shopId, placeId, session),
  chargerCouverture: (latitude, longitude) =>
    fetchCoverage(shopId, latitude, longitude),
});

const UTM = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
];

/**
 * D'où vient la demande : l'adresse de la page qui a envoyé le client
 * (`document.referrer`, seulement si c'est une adresse web) et les paramètres
 * UTM de cette page, les cinq clés que l'api accepte. Rien d'autre : jamais de
 * donnée du client dans l'adresse. Sans navigateur, ou sans rien de tout ça,
 * un objet vide.
 */
export function origineDeLaPage() {
  if (typeof window === 'undefined') return {};
  const origine = {};
  try {
    // Dans un cadre, le navigateur réduit le referrer à l'origine du site : le
    // script du réparateur passe donc l'adresse exacte de la page en `?origin=`.
    const voulue = new URLSearchParams(window.location.search).get('origin');
    const ref = /^https?:\/\//i.test(voulue) ? voulue : document.referrer;
    if (/^https?:\/\//i.test(ref)) origine.origin_url = ref.slice(0, 2048);
  } catch (e) {
    // rien
  }
  try {
    const params = new URLSearchParams(window.location.search);
    const utm = {};
    UTM.forEach((cle) => {
      const valeur = params.get(cle);
      if (valeur) utm[cle] = valeur.slice(0, 255);
    });
    if (Object.keys(utm).length) origine.utm = utm;
  } catch (e) {
    // rien
  }
  return origine;
}

/**
 * Sur `/reserver/:slug` : `public/{slug}/site`, anonyme, sans jeton. `cadre` :
 * la page tourne dans le cadre du script du réparateur ; la demande le dit à
 * l'api en `embed` (FIXERLIGHT-062), `false` quand la page est ouverte seule.
 */
export const accesSlug = (slug, { cadre = false } = {}) => ({
  cle: slug,
  chargerCompte: () => fetchPublicSlug(slug),
  chargerDispo: (from, to, service) =>
    fetchAvailabilitySlug(slug, from, to, service),
  envoyer: (body) =>
    postBookingSlug(slug, {
      ...body,
      ...origineDeLaPage(),
      embed: cadre === true,
    }),
  confirmerCode: (token, code) => confirmCodeSlug(slug, token, code),
  renvoyerCode: (token) => resendCodeSlug(slug, token),
  suggererAdresses: (texte, session) =>
    suggestAddressesSlug(slug, texte, session),
  detaillerAdresse: (placeId, session) =>
    fetchAddressSlug(slug, placeId, session),
  chargerCouverture: (latitude, longitude) =>
    fetchCoverageSlug(slug, latitude, longitude),
});
